import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/news", label: "动漫资讯", end: false },
  { to: "/anime", label: "动漫作品", end: false },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? "bg-background-50/95 backdrop-blur border-b border-background-200/70"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-6 h-16 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading font-extrabold text-lg">
              h
            </span>
            <span className="font-heading font-extrabold text-lg text-foreground-950 tracking-tight">
              h动漫
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "text-primary-600 bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex items-center flex-1 max-w-sm ml-auto">
            <div className="relative w-full">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索动漫资讯或作品"
                className="w-full h-9 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300"
              />
            </div>
          </form>

          <div className="hidden md:flex items-center gap-1 ml-2">
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive ? "text-accent-700" : "text-foreground-700 hover:text-accent-700"
                }`
              }
            >
              关于我们
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive ? "text-accent-700" : "text-foreground-700 hover:text-accent-700"
                }`
              }
            >
              联系我们
            </NavLink>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)} />
        <aside
          className={`absolute top-16 left-0 right-0 bg-background-50 border-b border-background-200 p-4 transition-transform duration-300 ${
            mobileOpen ? "translate-y-0" : "-translate-y-4"
          }`}
        >
          <form onSubmit={submitSearch} className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索动漫资讯或作品"
              className="w-full h-11 pl-10 pr-3 rounded-md bg-background-100 border border-background-200 text-base focus:outline-none focus:border-accent-400"
            />
          </form>
          <nav className="flex flex-col">
            {[...navItems, { to: "/about", label: "关于我们", end: false }, { to: "/contact", label: "联系我们", end: false }].map(
              (item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-base font-medium cursor-pointer ${
                      isActive ? "text-primary-600 bg-primary-50" : "text-foreground-800"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
        </aside>
      </div>
    </>
  );
}